/**
 * Transform DRF accreditation-page response → GraphQL getAccreditations shape.
 *
 * The DRF endpoint returns { assessment: [...], certificate: [...] }
 * with flat metsCount/notMetsCount/completeCount fields.
 * Components expect Hasura aggregate shapes and indicatorsAggregate nesting.
 */

/**
 * Transform a single assessment from DRF to match Hasura shape.
 * @param {object} item - camelCased DRF assessment
 * @returns {object} - Hasura-compatible assessment
 */
function transformAssessment(item) {
  const result = { ...item }

  // Map flat counts → synthetic aggregate shapes
  result.mets = {
    aggregate: { count: item.metsCount || 0 },
  }
  result.notMets = {
    aggregate: { count: item.notMetsCount || 0 },
  }
  result.complete = {
    aggregate: { count: item.completeCount || 0 },
  }

  // Add indicatorsAggregate to survey_version if present
  if (result.accreditation?.surveyVersion) {
    result.accreditation.surveyVersion.indicatorsAggregate = {
      aggregate: { count: result.accreditation.indicatorsCount || 0 },
    }
  }
  if (result.surveyVisit?.accreditation?.surveyVersion) {
    result.surveyVisit.accreditation.surveyVersion.indicatorsAggregate = {
      aggregate: { count: result.surveyVisit.accreditation.indicatorsCount || 0 },
    }
  }

  return result
}

/**
 * Transform the full DRF accreditation-page response to match the Hasura shape.
 * @param {object} drf - camelCased response from GET /api/web.practice/{id}/accreditation-page/
 * @returns {object} - shape matching the existing getAccreditations GraphQL response
 */
export function transformAccreditationPage(drf) {
  if (!drf) return { assessment: [], certificate: [] }

  return {
    assessment: (drf.assessment || []).map(transformAssessment),
    certificate: drf.certificate || [],
  }
}
